import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { scroller } from "react-scroll";
import "./index.scss";

import CrypFrame from "../../assets/cryptradingframe.png";
import GiftCardFrame from "../../assets/giftcardframe.png";
import ComingSoonFrame from "../../assets/comingsoonframe.png";
import iphoneMockUp from "../../assets/iphone13mockup.png";
import Wallet from "../../assets/wallet.png";
import Padlock from "../../assets/padlock.png";
import Coin from "../../assets/coin.png";
import Clock from "../../assets/clock.png";

import {
  ArrowUpRight,
  HelpCircle,
  ChevronDown,
  ChevronUp,
  Sparkles,
  CheckCircle2,
  ShieldCheck,
} from "lucide-react";

import ContactUs from "./ContactUs";
import Testimonial from "./Testimonials";

const GET_STARTED_URL = "https://app.thespherex.com/";

const FAQS = [
  {
    question: "How fast do I receive cash in my bank account?",
    answer:
      "Trade payouts are processed automatically via instant bank transfer. Once your crypto transaction is confirmed on the blockchain or your gift card is verified, funds arrive in your Nigerian bank account within 2 to 5 minutes.",
  },
  {
    question: "Which cryptocurrencies can I sell on SphereX?",
    answer:
      "You can sell USDT (TRC20, ERC20, BEP20), Bitcoin (BTC), Ethereum (ETH), and Solana (SOL). All crypto sales are credited at competitive market exchange rates.",
  },
  {
    question: "What types of gift cards are accepted?",
    answer:
      "We accept major international gift cards including Steam, Apple/iTunes, Amazon, American Express (Amex), Vanilla Visa, Sephora, Razer Gold, eBay, and Nike (both physical cards with receipts and electronic e-codes).",
  },
  {
    question: "Are there any hidden withdrawal charges or deductions?",
    answer:
      "None at all. The estimated payout amount displayed on your trade summary is exactly what lands in your bank account. We believe in 100% transparent pricing with zero surprise fees.",
  },
  {
    question: "Is SphereX safe and legitimate?",
    answer:
      "Yes. SphereX uses bank-grade 256-bit SSL encryption, automated escrow mechanisms, and verified Nigerian banking channels to ensure your personal information and payouts are completely secure.",
  },
  {
    question: "How can I contact customer support if I need help?",
    answer:
      "Our support agents are active 24/7. You can reach us via our support hotline (+234 702 544 6361), email (support@thespherex.com), or through the direct contact form on our website.",
  },
];

const LandingPage = () => {
  const location = useLocation();
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const targetSection = params.get("scrollTo");
    if (targetSection) {
      setTimeout(() => {
        scroller.scrollTo(targetSection, {
          smooth: true,
          duration: 500,
          offset: -80,
        });
      }, 150);
    }
  }, [location]);

  return (
    <div className="landing_page_container pb-3 bg-white text-gray-900">

      {/* 1. HERO SECTION */}
      <section className="section1 relative overflow-hidden pt-28 pb-16 md:pt-36 md:pb-20 bg-gradient-to-b from-purple-50/60 via-slate-50/20 to-white" id="home">
        {/* Soft Ambient Glow Elements (No background image) */}
        <div className="absolute top-0 right-1/4 w-96 h-96 bg-purple-200/40 rounded-full blur-3xl pointer-events-none -z-10" />
        <div className="absolute top-40 -left-20 w-80 h-80 bg-indigo-200/25 rounded-full blur-3xl pointer-events-none -z-10" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
            {/* Left Content */}
            <div className="lg:col-span-7 text-center lg:text-left">
              {/* Pill Badge */}
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-purple-100/80 border border-purple-200 text-purple-700 text-xs font-extrabold uppercase tracking-wider mb-6 shadow-sm">
                <Sparkles className="w-3.5 h-3.5 text-purple-600" />
                <span>Nigeria's Trusted Exchange</span>
              </div>

              {/* Exact Copy Heading */}
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-gray-900 tracking-tight leading-[1.12]">
                Convert your Crypto & Gift Cards to <span className="text-purple-600 bg-gradient-to-r from-purple-600 via-indigo-600 to-purple-800 bg-clip-text text-transparent">Naira Easily</span>
              </h1>

              {/* Exact Copy Subheading */}
              <p className="text-gray-600 font-bold text-lg sm:text-xl md:text-2xl mt-5 leading-relaxed">
                Fast, Safe and Easy Trading.
              </p>

              {/* Exact Copy CTA */}
              <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 pt-8">
                <a
                  href={GET_STARTED_URL}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2.5 bg-purple-600 hover:bg-purple-700 text-white font-bold text-base px-9 py-4 rounded-full shadow-lg shadow-purple-600/25 hover:shadow-xl hover:shadow-purple-600/35 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200"
                >
                  <span>Get Started</span>
                  <ArrowUpRight className="w-5 h-5" />
                </a>

                <button
                  onClick={() =>
                    scroller.scrollTo("how-it-works", {
                      smooth: true,
                      duration: 500,
                      offset: -80,
                    })
                  }
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-white hover:bg-gray-50 border border-gray-200 text-gray-700 font-bold text-base shadow-sm hover:border-gray-300 transition-all"
                >
                  <span>How It Works</span>
                </button>
              </div>

              {/* Trust Metric Strip */}
              <div className="pt-10 mt-6 grid grid-cols-3 gap-4 border-t border-gray-100 max-w-lg mx-auto lg:mx-0">
                <div className="text-center lg:text-left">
                  <p className="text-lg sm:text-2xl font-black text-gray-900">2 - 5 Mins</p>
                  <p className="text-xs text-gray-500 font-medium">Instant Payout</p>
                </div>
                <div className="text-center lg:text-left border-x border-gray-100 px-3">
                  <p className="text-lg sm:text-2xl font-black text-purple-600">₦0 Fees</p>
                  <p className="text-xs text-gray-500 font-medium">Zero Hidden Charges</p>
                </div>
                <div className="text-center lg:text-left">
                  <p className="text-lg sm:text-2xl font-black text-gray-900">24 / 7</p>
                  <p className="text-xs text-gray-500 font-medium">Support Online</p>
                </div>
              </div>
            </div>

            {/* Right Column: Live Trading Preview Card & Phone Showcase */}
            <div className="lg:col-span-5 relative flex items-center justify-center">
              {/* Ambient backdrop glow */}
              <div className="absolute inset-0 bg-gradient-to-tr from-purple-500/20 to-indigo-500/20 rounded-3xl filter blur-2xl opacity-70 transform -rotate-1" />

              {/* Live Preview Card */}
              <div className="relative w-full max-w-md bg-white rounded-3xl p-6 sm:p-7 shadow-2xl shadow-purple-500/10 border border-gray-100 space-y-4">
                {/* Card Top */}
                <div className="flex items-center justify-between border-b border-gray-100 pb-3.5">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-xl bg-purple-100 text-purple-700 flex items-center justify-center font-black text-xs">
                      SX
                    </div>
                    <div>
                      <h4 className="text-sm font-extrabold text-gray-900">Instant Trade Estimate</h4>
                      <p className="text-[11px] text-gray-400">Guaranteed Live Exchange Rate</p>
                    </div>
                  </div>
                  <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-200">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                    Live Rate
                  </span>
                </div>

                {/* You Sell Section */}
                <div className="p-3.5 rounded-2xl bg-gray-50/90 border border-gray-100 space-y-1">
                  <div className="flex justify-between items-center text-xs font-semibold text-gray-500">
                    <span>You Sell</span>
                    <span>Rate: ₦1,530 / $</span>
                  </div>
                  <div className="flex justify-between items-center pt-1">
                    <span className="text-2xl font-black text-gray-900">500.00</span>
                    <div className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-xl border border-gray-200 shadow-sm">
                      <span className="w-2 h-2 rounded-full bg-emerald-500" />
                      <span className="text-xs font-extrabold text-gray-800">USDT</span>
                    </div>
                  </div>
                </div>

                {/* Exchange arrow indicator */}
                <div className="flex justify-center -my-2">
                  <div className="w-7 h-7 rounded-full bg-purple-600 text-white flex items-center justify-center shadow-md shadow-purple-600/30">
                    <ChevronDown className="w-4 h-4" />
                  </div>
                </div>

                {/* You Receive in Naira */}
                <div className="p-3.5 rounded-2xl bg-purple-50/70 border border-purple-100 space-y-1">
                  <div className="flex justify-between items-center text-xs font-semibold text-purple-700">
                    <span>You Receive (Instant Bank Transfer)</span>
                    <span className="font-bold text-emerald-600">Zero Fee</span>
                  </div>
                  <div className="flex justify-between items-center pt-1">
                    <span className="text-2xl sm:text-3xl font-black text-purple-900">₦765,000.00</span>
                    <div className="flex items-center gap-1 bg-white px-2.5 py-1 rounded-xl border border-purple-200 shadow-sm">
                      <span className="text-xs font-extrabold text-purple-700">NGN</span>
                    </div>
                  </div>
                </div>

                {/* Bank Payout Channel */}
                <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50 border border-gray-100 text-xs text-gray-600">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    <span className="font-medium">Direct to Any Nigerian Bank</span>
                  </div>
                  <span className="text-[11px] font-bold text-gray-400">2 - 5 min</span>
                </div>

                {/* Direct Action Link */}
                <a
                  href={GET_STARTED_URL}
                  className="w-full flex items-center justify-center gap-2 bg-gray-900 hover:bg-black text-white font-bold py-3 px-4 rounded-2xl transition shadow-md text-xs sm:text-sm"
                >
                  <span>Sell at This Rate</span>
                  <ArrowUpRight className="w-4 h-4" />
                </a>
              </div>

              {/* Floating Pill: Bank Alert */}
              <div className="hidden sm:flex absolute -top-3 -right-3 bg-white/95 backdrop-blur-sm p-2.5 px-3 rounded-2xl shadow-xl border border-gray-100 items-center gap-2.5">
                <div className="w-7 h-7 rounded-xl bg-emerald-100 text-emerald-600 flex items-center justify-center">
                  <CheckCircle2 className="w-4 h-4" />
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase text-gray-400">Bank Alert</p>
                  <p className="text-xs font-black text-gray-900">+₦765,000.00</p>
                </div>
              </div>

              {/* Floating Pill: Escrow Protected */}
              <div className="hidden sm:flex absolute -bottom-3 -left-3 bg-white/95 backdrop-blur-sm p-2.5 px-3 rounded-2xl shadow-xl border border-gray-100 items-center gap-2.5">
                <div className="w-7 h-7 rounded-xl bg-purple-100 text-purple-600 flex items-center justify-center">
                  <ShieldCheck className="w-4 h-4" />
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase text-gray-400">Protected</p>
                  <p className="text-xs font-black text-gray-900">100% Safe Escrow</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. OUR SERVICES */}
      <div className="section2 mt-16 pt-10 px-5 md:px-12" id="services">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <h2 className="text-3xl md:text-5xl font-extrabold text-gray-900 tracking-tight">
            Our Services
          </h2>
          <p className="text-center mt-3 text-gray-600 font-semibold text-base">
            Sell your Crypto and Gift Cards at premium market rates.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">

          {/* Card 1: Crypto */}
          <div className="text-center flex flex-col items-center hover:scale-105 transition-all bg-slate-50/80 hover:bg-white p-6 rounded-3xl border border-gray-100 hover:border-purple-200 hover:shadow-xl">
            <div className="bg-gray-100 px-4 pt-4 rounded-2xl w-full flex items-center justify-center">
              <img src={CrypFrame} alt="Crypto Trading" className="w-full object-contain" />
            </div>
            <div className="mt-4">
              <h3 className="font-bold text-xl text-gray-900">Crypto Trading</h3>
              <h4 className="mt-1 text-purple-700 font-semibold text-sm">
                Convert your crypto to cash easily
              </h4>
              <p className="text-gray-500 text-xs mt-1">Get high rates and fast bank payment</p>
            </div>
          </div>

          {/* Card 2: Gift Cards */}
          <div className="text-center flex flex-col items-center hover:scale-105 transition-all bg-slate-50/80 hover:bg-white p-6 rounded-3xl border border-gray-100 hover:border-purple-200 hover:shadow-xl">
            <div className="bg-gray-100 px-4 pt-4 rounded-2xl w-full flex items-center justify-center">
              <img src={GiftCardFrame} alt="Gift Cards" className="w-full object-contain" />
            </div>
            <div className="mt-4">
              <div className="flex items-center justify-center gap-2">
                <h3 className="font-bold text-xl text-gray-900">Gift Cards</h3>
                <span className="text-[10px] font-bold text-amber-700 bg-amber-100 px-2.5 py-0.5 rounded-full">
                  Launching Soon
                </span>
              </div>
              <h4 className="mt-1 text-purple-700 font-semibold text-sm">
                Sell your gift cards with no issues
              </h4>
              <p className="text-gray-500 text-xs mt-1">Get sweet rates and fast payment</p>
            </div>
          </div>

          {/* Card 3: Bill Payments */}
          <div className="text-center flex flex-col items-center hover:scale-105 transition-all bg-slate-50/80 hover:bg-white p-6 rounded-3xl border border-gray-100 hover:border-purple-200 hover:shadow-xl">
            <div className="bg-gray-100 px-4 pt-4 rounded-2xl w-full flex items-center justify-center">
              <img src={ComingSoonFrame} alt="Bill Payments" className="w-full object-contain" />
            </div>
            <div className="mt-4">
              <div className="flex items-center justify-center gap-2">
                <h3 className="font-bold text-xl text-gray-900">Bill Payments</h3>
                <span className="text-[10px] font-bold text-indigo-700 bg-indigo-100 px-2.5 py-0.5 rounded-full">
                  Launching Soon
                </span>
              </div>
              <h4 className="mt-1 text-purple-700 font-semibold text-sm">
                Buy airtime and data bundles
              </h4>
              <p className="text-gray-500 text-xs mt-1">Pay utilities directly from your wallet</p>
            </div>
          </div>

        </div>

        <div className="w-full flex justify-center mt-12">
          <a
            href={GET_STARTED_URL}
            className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white font-bold text-sm px-8 py-3.5 rounded-full shadow-md hover:scale-105 transition-all"
          >
            <span>Get Started Now</span>
            <ArrowUpRight className="w-4 h-4" />
          </a>
        </div>
      </div>

      {/* 3. SELL & CASH OUT FAST (STICKY / SWERVING IPHONE 13) */}
      <div
        className="section3 mt-24 py-12 px-5 md:px-12 max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-12"
        id="how-it-works"
      >
        {/* Left Content */}
        <div className="w-full md:w-1/2 text-center md:text-left space-y-5">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-purple-100 text-purple-700 text-xs font-bold uppercase tracking-wider">
            <Sparkles className="w-3.5 h-3.5" />
            <span>Fast & Simple</span>
          </div>

          <h2 className="text-3xl md:text-5xl font-extrabold text-gray-900 tracking-tight leading-tight">
            Sell, and cash out fast!
          </h2>

          <p className="text-base text-gray-600 font-medium leading-relaxed max-w-md mx-auto md:mx-0">
            Sell Crypto and gift cards. Fast, legit, and simple. Trade smarter with SphereX today!
          </p>

          <div className="pt-2">
            <a
              href={GET_STARTED_URL}
              className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white font-bold text-sm px-8 py-3.5 rounded-full shadow-md hover:scale-105 transition-all duration-200"
            >
              <span>Get Started</span>
              <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        </div>

        {/* Right Column: Swerving iPhone Mockup */}
        <div className="w-full md:w-1/2 flex items-center justify-center swerve-container">
          <img
            src={iphoneMockUp}
            alt="SphereX iPhone"
            className="w-full max-w-lg swerve-img drop-shadow-2xl"
          />
        </div>
      </div>

      {/* 4. WHY TRADE WITH US */}
      <div className="section4 mt-20 px-5 md:px-12 max-w-7xl mx-auto" id="why-us">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <h2 className="text-3xl md:text-5xl font-extrabold text-gray-900 tracking-tight">
            Why Trade With Us?
          </h2>
          <p className="mt-3 text-gray-600 font-semibold text-base">
            Trade your Crypto, sell your Gift Cards, recharge your Airtime and Data all in one place.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
          <StatCard
            title="Instant Payment"
            desc="Enjoy instant cashout when you trade crypto and gift cards. No dulling!"
            icon={<img src={Wallet} alt="Wallet" className="w-16 h-16 object-contain" />}
          />
          <StatCard
            title="Sweet Bonus"
            desc="Trade smarter, earn faster! Get an instant bonus on every transaction above $1,000."
            icon={<img src={Coin} alt="Bonus" className="w-16 h-16 object-contain" />}
          />
          <StatCard
            title="Easy and Secure"
            desc="Enjoy legit and secure trades with every transaction you make. No wahala!"
            icon={<img src={Padlock} alt="Security" className="w-16 h-16 object-contain" />}
          />
          <StatCard
            title="Active 24/7"
            desc="Stay in control of your trades and get in touch with us anyday, anytime."
            icon={<img src={Clock} alt="Support" className="w-16 h-16 object-contain" />}
          />
        </div>
      </div>

      {/* 5. CUSTOMER TESTIMONIALS */}
      <Testimonial />

      {/* 6. FAQ ACCORDION */}
      <section className="py-20 bg-slate-50 border-t border-gray-100 px-4 sm:px-6 lg:px-8" id="faq">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-purple-100 text-purple-700 text-xs font-bold uppercase tracking-wider mb-3">
              <HelpCircle className="w-3.5 h-3.5" />
              <span>Got Questions?</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 tracking-tight">
              Frequently Asked Questions
            </h2>
            <p className="mt-3 text-base text-gray-600">
              Everything you need to know about trading on SphereX.
            </p>
          </div>

          <div className="space-y-3.5">
            {FAQS.map((faq, index) => {
              const isOpen = openFaq === index;
              return (
                <div
                  key={index}
                  className="bg-white rounded-2xl border border-gray-200/80 overflow-hidden shadow-sm transition-all"
                >
                  <button
                    onClick={() => setOpenFaq(isOpen ? -1 : index)}
                    className="w-full px-6 py-4 text-left flex items-center justify-between gap-4 focus:outline-none"
                    aria-expanded={isOpen}
                  >
                    <span className="text-base font-bold text-gray-900">
                      {faq.question}
                    </span>
                    <span className="p-1 rounded-full bg-gray-100 text-gray-600 shrink-0">
                      {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </span>
                  </button>

                  {isOpen && (
                    <div className="px-6 pb-5 text-sm text-gray-600 leading-relaxed border-t border-gray-100 pt-3 animate-fadeIn">
                      {faq.answer}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 7. CONTACT US */}
      <ContactUs />

      {/* 8. PRE-FOOTER CTA STRIP */}
      <section className="py-16 bg-gradient-to-r from-purple-700 via-purple-600 to-indigo-700 text-white px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto text-center space-y-6">
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight">
            Ready to Cash Out at Sweet Rates?
          </h2>
          <p className="text-purple-100 text-base sm:text-lg max-w-xl mx-auto font-normal">
            Join thousands of satisfied traders converting crypto and gift cards to Naira in minutes.
          </p>
          <div className="pt-2 flex items-center justify-center">
            <a
              href={GET_STARTED_URL}
              className="inline-flex items-center justify-center gap-2 bg-white hover:bg-gray-100 text-purple-700 font-extrabold text-base px-9 py-4 rounded-full shadow-lg transition-all duration-200 hover:scale-105"
            >
              <span>Get Started Now</span>
              <ArrowUpRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

    </div>
  );
};

const StatCard = ({ title, desc, icon }) => (
  <div className="bg-white p-6 rounded-2xl shadow-md border border-gray-100 flex items-center gap-5 hover:scale-105 transition-all">
    <div className="shrink-0">{icon}</div>
    <div>
      <h3 className="text-lg font-bold text-gray-900 mb-1">
        {title}
      </h3>
      <p className="text-sm text-gray-600 font-medium leading-relaxed">
        {desc}
      </p>
    </div>
  </div>
);

export default LandingPage;
